import React, { useState, useEffect } from 'react';

const PriceRangeStep = ({ 
    priceRange, 
    setPriceRange,
    selectedPriceRangeLabel,
    setSelectedPriceRangeLabel,
    handlePriceRangeButtonClick,
    handleEnter,
    onNext,
    onBack
}) => {
    // State for dual slider values
    const [minPrice, setMinPrice] = useState(20);
    const [maxPrice, setMaxPrice] = useState(50);
    const [noMaxLimit, setNoMaxLimit] = useState(false);

    // Update the price range string when slider values change
    useEffect(() => {
        const priceRangeString = noMaxLimit ? `£${minPrice}+` : `£${minPrice} - £${maxPrice}`;
        setPriceRange(priceRangeString);
        setSelectedPriceRangeLabel(priceRangeString);
    }, [minPrice, maxPrice, noMaxLimit, setPriceRange, setSelectedPriceRangeLabel]); // Depend on min, max price, and no limit flag

    // Handle min price slider change
    const handleMinPriceChange = (event) => {
        const newMin = parseInt(event.target.value);
        // Ensure min is at least 10 less than max
        if (newMin <= maxPrice - 10) {
            setMinPrice(newMin);
            setNoMaxLimit(false); // Reset no limit when manually adjusting
        }
    };

    // Handle max price slider change
    const handleMaxPriceChange = (event) => {
        const newMax = parseInt(event.target.value);
        // Ensure max is at least 10 more than min
        if (newMax >= minPrice + 10) {
            setMaxPrice(newMax);
            setNoMaxLimit(false); // Reset no limit when manually adjusting
        }
    };

    // Handle preset button clicks
    const handlePresetClick = (minVal, maxVal, noLimit = false) => {
        setMinPrice(minVal);
        setMaxPrice(maxVal);
        setNoMaxLimit(noLimit);
    };

    // Handle "No Limit" button click
    const handleNoLimitClick = () => {
        setMinPrice(50); // Set a reasonable minimum for "no limit"
        setMaxPrice(500); // Keep max at slider maximum for display purposes
        setNoMaxLimit(true);
    };

    // Calculate percentage positions for the range fill
    const minPercent = (minPrice / 500) * 100;
    const maxPercent = (maxPrice / 500) * 100;

    return (
        <div className="step-container">
            <div className="form-step">
                <h2>What's your budget range for wine?</h2>
                <div className="price-selection">
                    <div className="price-display">
                        <span className="price-range-display">
                            {noMaxLimit ? `£${minPrice}+` : `£${minPrice} - £${maxPrice}`}
                        </span>
                    </div>
                    <div className="slider-group">
                        <div className="dual-range-container">
                            <div className="dual-range-slider">
                                {/* Background track */}
                                <div className="slider-track"></div>
                                
                                {/* Active range fill */}
                                <div 
                                    className="slider-range" 
                                    style={{
                                        left: `${minPercent}%`,
                                        width: `${maxPercent - minPercent}%`
                                    }}
                                ></div>
                                
                                {/* Min value slider */}
                                <input
                                    type="range"
                                    id="min-price-slider"
                                    min="0"
                                    max="500"
                                    step="10"
                                    value={minPrice}
                                    onChange={handleMinPriceChange}
                                    className="price-slider price-slider-min"
                                />
                                
                                {/* Max value slider */}
                                <input
                                    type="range"
                                    id="max-price-slider"
                                    min="0"
                                    max="500"
                                    step="10"
                                    value={maxPrice}
                                    onChange={handleMaxPriceChange}
                                    className="price-slider price-slider-max"
                                />
                            </div>
                            
                            <div className="slider-labels">
                                <span>£0</span>
                                <span>£500</span>
                            </div>
                    
                        </div>
                    </div>
                    
                    <div className="quick-presets">
                            <span className="preset-label">Quick presets:</span>
                            <div className="preference-buttons">
                                <button 
                                    className={`pref-button ${minPrice === 0 && maxPrice === 30 ? 'selected' : ''}`}
                                    onClick={() => handlePresetClick(0, 30)}
                                >
                                    Under £30
                                </button>
                                <button 
                                    className={`pref-button ${minPrice === 30 && maxPrice === 50 ? 'selected' : ''}`}
                                    onClick={() => handlePresetClick(30, 50)}
                                >
                                    £30-50
                                </button>
                                <button 
                                    className={`pref-button ${minPrice === 50 && maxPrice === 100 ? 'selected' : ''}`}
                                    onClick={() => handlePresetClick(50, 100)}
                                >
                                    £50-100
                                </button>
                                <button 
                                    className={`pref-button ${minPrice === 100 && maxPrice === 200 ? 'selected' : ''}`}
                                    onClick={() => handlePresetClick(100, 200)}
                                >
                                    £100-200
                                </button>
                                <button 
                                    className={`pref-button ${minPrice === 200 && maxPrice === 500 ? 'selected' : ''}`}
                                    onClick={() => handlePresetClick(200, 500)}
                                >
                                    £200-500
                                </button>
                                <button 
                                    className={`pref-button ${noMaxLimit ? 'selected' : ''}`}
                                    onClick={handleNoLimitClick}
                                >
                                    No limit
                                </button>
                            </div>
                        </div>
                </div>
                <div className="navigation-buttons">
                    <button onClick={onBack}>Back</button>
                    <button onClick={onNext}>Get Recommendations</button>
                </div>
            </div>
        </div>
    );
};

export default PriceRangeStep;
